import { Link } from "react-router-dom";
import { ArrowRight, Activity, Scissors, HeartPulse, Baby, Siren, Microscope, ShieldCheck, Building2 } from "lucide-react";
import Placeholder from "@/components/Placeholder";
import { Reveal, StaggerGroup, StaggerItem, GlowCard, AnimatedLine, FloatingBlob, Magnetic } from "@/components/Motion";
import { facilities } from "@/lib/data";
import SEO from "@/components/SEO";

const iconMap = { Activity, Scissors, HeartPulse, Baby, Siren, Microscope };

export default function WellMed() {
  return (
    <div data-testid="wellmed-page" className="relative overflow-hidden">
      <SEO title="WellMed Hospitals" description="Discover our NABH-accredited multi-specialty hospital equipped with modern OTs and NICU." />
      <FloatingBlob color="aurora-coral" size={500} top="-10%" left="-10%" delay={0} />

      <section className="py-16 lg:py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-7">
            <div className="overline mb-4">WellMed Hospitals · Gurugram</div>
            <h1 className="font-heading text-5xl md:text-6xl text-[var(--color-text)] leading-[1.05]">
              World-class healthcare, <em className="text-[var(--color-coral)] not-italic">with compassion at the core.</em>
            </h1>
            <p className="mt-6 text-lg text-[var(--color-text-muted)] max-w-2xl leading-relaxed">
              A 50-bed NABH-accredited multi-specialty hospital in Sector 39, Gurugram — co-founded by Dr. Priyanka Gupta. Built to combine advanced medical technology with a calming, patient-friendly environment.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <span className="px-4 py-2 rounded-full bg-[var(--color-coral-light)] text-[var(--color-coral)] border border-[var(--color-coral)]/30 text-xs font-semibold tracking-wider uppercase">NABH Accredited</span>
              <span className="px-4 py-2 rounded-full bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border-2)] text-xs font-semibold tracking-wider uppercase">50-Bed Multi-Specialty</span>
              <span className="px-4 py-2 rounded-full bg-[var(--color-surface-2)] border border-[var(--color-border)] text-[var(--color-text-muted)] text-xs font-semibold tracking-wider uppercase">11 Centres of Excellence</span>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.2}>
            <div className="img-hover-scale rounded-2xl border border-[var(--color-border-2)]">
              <Placeholder variant="wide" label="WellMed Hospital Exterior" Icon={Building2} testId="wellmed-hero-img" src="/images/facilities/DSC04279.webp" />
            </div>
          </Reveal>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      <section className="py-16 lg:py-24 bg-[var(--color-surface)] relative">
        <FloatingBlob color="aurora-purple" size={400} bottom="-5%" right="-5%" delay={1} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            <Reveal>
              <div className="overline mb-4">Our Story</div>
              <h2 className="font-heading text-4xl md:text-5xl text-[var(--color-text)] leading-tight">A vision realized.</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="text-base md:text-lg text-[var(--color-text-muted)] leading-relaxed space-y-4">
                <p>
                  At WellMed Hospitals, your healing is the highest priority. Designed with recovery in mind, the state-of-the-art infrastructure creates a calming, patient-friendly environment that supports faster healing.
                </p>
                <p>
                  A team of skilled doctors — each with over a decade of experience — combines clinical depth with a quiet, family-like warmth. Every nurse, technician and consultant shares one mission: helping you get better, every single day.
                </p>
              </div>
            </Reveal>
          </div>

          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" stagger={0.1}>
            <StaggerItem><div className="img-hover-scale rounded-2xl border border-[var(--color-border)]"><Placeholder variant="square" label="Reception" src="/images/facilities/DSC04277.webp" /></div></StaggerItem>
            <StaggerItem><div className="img-hover-scale rounded-2xl border border-[var(--color-border)]"><Placeholder variant="square" label="Patient Room" src="/images/facilities/DSC04275.webp" /></div></StaggerItem>
            <StaggerItem><div className="img-hover-scale rounded-2xl border border-[var(--color-border)]"><Placeholder variant="square" label="Modular OT" src="/images/facilities/DSC04315.webp" /></div></StaggerItem>
            <StaggerItem><div className="img-hover-scale rounded-2xl border border-[var(--color-border)]"><Placeholder variant="square" label="NICU" src="/images/facilities/DSC04318.webp" /></div></StaggerItem>
          </StaggerGroup>
        </div>
      </section>

      <AnimatedLine className="mx-6 lg:mx-16" />

      <section className="py-16 lg:py-24 relative">
        <FloatingBlob color="aurora-warm" size={350} top="15%" left="5%" delay={2} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <Reveal>
            <div className="overline mb-4">Facilities</div>
            <h2 className="font-heading text-4xl md:text-5xl text-[var(--color-text)] mb-12 max-w-3xl leading-tight">State-of-the-art medical facilities.</h2>
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" stagger={0.1}>
            {facilities.map((f) => {
              const I = iconMap[f.icon] || HeartPulse;
              return (
                <StaggerItem key={f.title}>
                  <GlowCard className="p-7 h-full group">
                    <div className="w-11 h-11 rounded-xl bg-[var(--color-coral-light)] flex items-center justify-center mb-5 border border-[var(--color-coral)]/20 group-hover:bg-[var(--color-coral)] transition-colors duration-300">
                      <I className="w-5 h-5 text-[var(--color-coral)] group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading text-2xl text-white">{f.title}</h3>
                    <p className="text-sm text-white/80 mt-2 leading-relaxed">{f.desc}</p>
                  </GlowCard>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[var(--color-surface)] border-y border-[var(--color-border)] relative">
        <Reveal>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-8 text-center relative z-10">
            {[
              { num: "50+", label: "Medical Professionals" },
              { num: "50", label: "Bed Capacity" },
              { num: "24/7", label: "Emergency Services" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-heading text-6xl md:text-7xl text-[var(--color-coral)]">{s.num}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--color-text-faint)] mt-3 font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="py-16 lg:py-24 relative">
        <FloatingBlob color="aurora-coral" size={400} bottom="-5%" left="40%" delay={3} />
        <Reveal>
          <div className="max-w-5xl mx-auto px-6 lg:px-8 relative">
            <div className="glass-card rounded-3xl p-10 lg:p-14 relative overflow-hidden border border-[var(--color-coral)]/30">
              <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[var(--color-coral-light)] animate-[float-slow_8s_ease-in-out_infinite]" />
              <div className="relative">
                <ShieldCheck className="w-9 h-9 text-[var(--color-coral)] mb-5" strokeWidth={1.5} />
                <h2 className="font-heading text-3xl md:text-4xl text-white">Premium care, close to home.</h2>
                <p className="mt-4 text-white/80 max-w-xl">
                  Visit WellMed Hospitals at 242, Sector 39, Gurugram, Haryana 122003. Open 24×7 for emergencies.
                </p>
                <div className="mt-7 flex flex-wrap gap-4">
                  <Magnetic><Link to="/appointment" data-testid="wellmed-cta-book" className="btn-primary">Book Appointment <ArrowRight className="w-4 h-4" /></Link></Magnetic>
                  <Magnetic><Link to="/contact" data-testid="wellmed-cta-contact" className="btn-outline">Get Directions</Link></Magnetic>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
